import type { CSSProperties, ReactNode } from "react";
import { cn } from "../../lib/utils";

const SECONDS_PER_ITEM = 10;

export interface NewsTickerProps {
  items: ReactNode[];
  /** Accessible name of the ticker region. */
  label?: string;
  className?: string;
}

function TickerGroup({
  items,
  duplicate,
}: Readonly<{ items: ReactNode[]; duplicate?: boolean }>) {
  return (
    <ul
      // The duplicate only closes the loop visually: hidden from assistive
      // technology and keyboard focus so items and links exist only once.
      aria-hidden={duplicate || undefined}
      className={cn(
        "flex min-w-full shrink-0 animate-news-ticker items-center justify-around group-focus-within:[animation-play-state:paused] group-hover:[animation-play-state:paused]",
        "motion-reduce:shrink motion-reduce:animate-none motion-reduce:flex-wrap motion-reduce:justify-center motion-reduce:gap-y-2",
        duplicate && "motion-reduce:hidden"
      )}
      data-testid={duplicate ? undefined : "news-ticker-items"}
      inert={duplicate || undefined}
    >
      {items.map((item, index) => (
        <li
          className="flex items-center gap-1.5 whitespace-nowrap px-[50px] motion-reduce:justify-center motion-reduce:whitespace-normal motion-reduce:text-center md:px-[200px] [&_a]:text-inherit [&_a]:underline [&_a]:underline-offset-2 [&_br]:hidden"
          // biome-ignore lint/suspicious/noArrayIndexKey: items are static rich text without IDs
          key={index}
        >
          <span aria-hidden="true">++</span>
          <span>{item}</span>
          <span aria-hidden="true">++</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Yellow informational band that scrolls its items in an endless loop. The
 * loop pauses while hovered or focused, so links stay clickable, and turns
 * into a static, wrapping list for users who prefer reduced motion.
 */
export function NewsTicker({
  items,
  label,
  className,
}: Readonly<NewsTickerProps>) {
  if (items.length === 0) {
    return null;
  }

  return (
    <section
      aria-label={label}
      className={cn(
        "group flex min-h-[52px] overflow-hidden bg-neonchartreuse py-2.5 font-bold font-brand text-black text-xs leading-[14px] tracking-[0.02em]",
        className
      )}
      data-testid="news-ticker"
      style={
        {
          "--news-ticker-duration": `${items.length * SECONDS_PER_ITEM}s`,
        } as CSSProperties
      }
    >
      <TickerGroup items={items} />
      <TickerGroup duplicate items={items} />
    </section>
  );
}
